import {
  LayoutDashboard,
  FileText,
  ClipboardCheck,
  FileSearch,
  ShoppingCart,
  PackageCheck,
  Receipt,
  Wallet,
  AlertTriangle,
  Bell,
  BarChart3,
  Users,
  Building2,
  ScrollText,
  ShieldCheck,
  Boxes,
  SlidersHorizontal,
  type LucideIcon,
} from "lucide-react";
import { type NavItem } from "@procureflow/shared-types";

// US-003b — Sidebar nav configuration + role-based filter.
//
// Each item carries a `navItem` tag from the permissions manifest
// (US-003a). filterNavForRole drops items whose tag is not in the
// role's allowed set; groups that end up empty disappear so we don't
// render bare group headings.
//
// Sub-screens (Payment Queue, Exceptions, Audit Logs, Finance &
// Payments) inherit visibility from a parent NavItem rather than
// gaining their own permission key — the manifest's screens are
// coarse-grained ("invoices", "reports") and these are sub-pages of
// those screens.

export interface NavItemConfig {
  to: string;
  label: string;
  icon: LucideIcon;
  navItem: NavItem;
}

export interface NavGroupConfig {
  group: string;
  items: readonly NavItemConfig[];
}

export const NAV_CONFIG: readonly NavGroupConfig[] = [
  {
    group: "Overview",
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, navItem: "dashboard" }],
  },
  {
    group: "Procurement",
    items: [
      { to: "/purchase-requests", label: "Purchase Requests", icon: FileText, navItem: "prs" },
      { to: "/approvals", label: "Approvals", icon: ClipboardCheck, navItem: "approval_queue" },
      { to: "/rfq", label: "RFQ Management", icon: FileSearch, navItem: "rfqs" },
      {
        to: "/purchase-orders",
        label: "Purchase Orders",
        icon: ShoppingCart,
        navItem: "purchase_orders",
      },
      // US-059 — item master catalogue (Admin / Procurement Officer).
      { to: "/item-master", label: "Item Master", icon: Boxes, navItem: "item_master" },
    ],
  },
  {
    group: "Operations",
    items: [
      { to: "/grn", label: "Goods Receipt", icon: PackageCheck, navItem: "grns" },
      { to: "/invoices", label: "Invoices", icon: Receipt, navItem: "invoices" },
      // Sub-screens of /invoices — inherit visibility from `invoices`.
      {
        to: "/invoices/payment-queue",
        label: "Payment Queue",
        icon: Wallet,
        navItem: "invoices",
      },
      { to: "/exceptions", label: "Exceptions", icon: AlertTriangle, navItem: "invoices" },
      // Finance & Payments sub-screen → groups under reports for now.
      { to: "/finance", label: "Finance & Payments", icon: Wallet, navItem: "reports" },
    ],
  },
  {
    group: "Vendor",
    items: [{ to: "/vendors", label: "Vendors", icon: Building2, navItem: "vendors" }],
  },
  {
    group: "Insights",
    items: [
      { to: "/notifications", label: "Notifications", icon: Bell, navItem: "notifications" },
      { to: "/reports", label: "Reports & Analytics", icon: BarChart3, navItem: "reports" },
    ],
  },
  {
    group: "Administration",
    items: [
      { to: "/users", label: "User Management", icon: Users, navItem: "user_management" },
      // US-061 — settings landing (functional currency + sub-settings links).
      { to: "/settings", label: "Settings", icon: SlidersHorizontal, navItem: "settings" },
      // US-054 — custom role management (Admin-only via the `settings`
      // navItem, same gate as Approval Rules / Audit Logs).
      { to: "/settings/roles", label: "Roles", icon: ShieldCheck, navItem: "settings" },
      {
        to: "/settings/approvals",
        label: "Approval Chains",
        icon: ClipboardCheck,
        navItem: "settings",
      },
      {
        to: "/approval-rules",
        label: "Approval Rules",
        icon: ClipboardCheck,
        navItem: "settings",
      },
      {
        to: "/settings/match-policy",
        label: "Match Policy",
        icon: ClipboardCheck,
        navItem: "match_policy",
      },
      // Audit Logs sit under settings — Admin-only nav exposure.
      { to: "/audit-logs", label: "Audit Logs", icon: ScrollText, navItem: "settings" },
    ],
  },
];

// ADR-017 §3 — filters on the user's resolved nav items (fetched from
// /api/me/permissions, with a static fallback) rather than a role.
export function filterNavForPermissions(
  nav: readonly NavGroupConfig[],
  allowedNavItems: readonly NavItem[],
): readonly NavGroupConfig[] {
  const allowed = new Set<NavItem>(allowedNavItems);
  return nav
    .map((g) => ({
      group: g.group,
      items: g.items.filter((i) => allowed.has(i.navItem)),
    }))
    .filter((g) => g.items.length > 0);
}
